import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/modules/shared/contexts/AuthContext';
import {
  programRoleMenuPermissionService,
  type MyProgramMenuAccessDto,
  type MenuKey,
  MENU_KEYS,
} from '../services/programRoleMenuPermissionService';
import { PROGRAM_ROUTE_SEGMENTS } from '@/modules/shared/constants/routes';

interface UseProgramRoleMenuPermissionsOptions {
  /** The program ID to load permissions for. */
  programId: string | undefined;
  /** Whether to enable the query. Default: true when programId is available. */
  enabled?: boolean;
  /**
   * Menu keys that must follow the program's real ProgramRoleMenuPermissions
   * configuration even for SuperAdmin/ProductAdmin, instead of their usual
   * blanket bypass. Use for menus meant to be opt-in per program (e.g. the
   * Overview/My Work workspace tabs) — everything else keeps the bypass.
   */
  ignoreSuperAdminFor?: readonly (MenuKey | string)[];
}

interface ProgramRoleMenuPermissionsResult {
  /** The full access DTO (role name, category, accessible keys). */
  menuAccess: MyProgramMenuAccessDto | null;
  /** Whether data is loading. */
  isLoading: boolean;
  /** Error message when permissions fail to load. */
  error: string | null;
  /** Whether the user has a role assigned in this program. */
  hasRoleAssignment: boolean;
  /** The program-scoped role name for display in the header. */
  programRoleName: string | null;
  /** The program-scoped role category. */
  roleCategory: string | null;
  /**
   * Returns true if the current user can access the given menu key.
   * PROGRAM_DASHBOARD is always accessible for any assigned user.
   */
  canAccessMenu: (menuKey: MenuKey | string) => boolean;
  /**
   * Checks whether the user can access a route segment.
   * Maps route segments to menu keys automatically.
   */
  canAccessRoute: (routeSegment: string) => boolean;
  /** Set of accessible menu keys for O(1) lookups. */
  accessibleMenuKeys: Set<string>;
  /** Refresh permissions (call after role or permission changes). */
  refetch: () => void;
  
  // ── Role Governance Properties ──
  /** Governance type: CONFIGURATION or OPERATIONAL */
  governanceType: string;
  /** True if this is a configuration-only role (e.g., Program Admin) */
  isConfigurationRole: boolean;
  /** True if this is an operational role (works through workspace) */
  isOperationalRole: boolean;
  /** Default landing route: "forms" for config roles, "workspace" for operational roles */
  defaultLandingRoute: string;
  /** Dashboard key resolved by backend. */
  dashboardKey: string;
  /** Absolute landing route resolved by backend. */
  landingRoute: string;
  /** Accessible action permission keys. */
  permissionKeys: Set<string>;
}

/**
 * Route segment → MenuKey mapping.
 * Used by canAccessRoute() to resolve route paths to menu keys.
 */
const ROUTE_TO_MENU_KEY: Record<string, string> = {
  // Core
  'select':                   MENU_KEYS.PROGRAM_SELECTION,
  [PROGRAM_ROUTE_SEGMENTS.DASHBOARD]: MENU_KEYS.PROGRAM_DASHBOARD,
  [PROGRAM_ROUTE_SEGMENTS.OPERATIONAL_DASHBOARD]: MENU_KEYS.OPERATIONAL_DASHBOARD,
  'management-dashboard':     MENU_KEYS.MANAGEMENT_DASHBOARD,
  'finance-dashboard':        MENU_KEYS.FINANCE_DASHBOARD,
  
  // Entities
  [PROGRAM_ROUTE_SEGMENTS.HOSPITALS]: MENU_KEYS.HOSPITALS,
  [PROGRAM_ROUTE_SEGMENTS.HCPS]: MENU_KEYS.HCPS,
  [PROGRAM_ROUTE_SEGMENTS.PATIENTS]: MENU_KEYS.PATIENTS,
  
  // Configuration
  [PROGRAM_ROUTE_SEGMENTS.ROLES]: MENU_KEYS.ROLES_CONFIG,
  [PROGRAM_ROUTE_SEGMENTS.USER_ASSIGNMENTS]: MENU_KEYS.USERS_CONFIG,
  [PROGRAM_ROUTE_SEGMENTS.FORMS]: MENU_KEYS.FORMS_CONFIG,
  [PROGRAM_ROUTE_SEGMENTS.WORKFLOW]: MENU_KEYS.WORKFLOW_CONFIG,
  [PROGRAM_ROUTE_SEGMENTS.ASSISTANCE_SETTINGS]: MENU_KEYS.ASSISTANCE_SETTINGS,
  [PROGRAM_ROUTE_SEGMENTS.PLANS4MODEL]: MENU_KEYS.FINANCIAL_PLAN,
  [PROGRAM_ROUTE_SEGMENTS.NOTIFICATIONS]: MENU_KEYS.NOTIFICATIONS_CONFIG,
  [PROGRAM_ROUTE_SEGMENTS.AUTOMATION_RULES]: MENU_KEYS.AUTOMATION_RULES,
  'notification-configured':  MENU_KEYS.NOTIFICATIONS_CONFIG,
  'review-queue':             MENU_KEYS.AUTOMATION_RULE_REVIEW,
  [PROGRAM_ROUTE_SEGMENTS.UI_SETTINGS]: MENU_KEYS.UI_SETTINGS,
  [PROGRAM_ROUTE_SEGMENTS.AUDIT_REPORT]: MENU_KEYS.AUDIT_REPORT,
  'role-form-workflow-mapping': MENU_KEYS.ROLE_FORM_WORKFLOW_MAPPING,
  [PROGRAM_ROUTE_SEGMENTS.LEGAL_PAGES]: MENU_KEYS.LEGAL_PAGES,
  
  // Workspace routes
  [PROGRAM_ROUTE_SEGMENTS.WORKSPACE]: MENU_KEYS.WORKSPACE,
  'pharma-workspace':         MENU_KEYS.PHARMA_WORKSPACE,
  'hospital-workspace':       MENU_KEYS.HOSPITAL_WORKSPACE,
  'hcp-workspace':            MENU_KEYS.HCP_WORKSPACE,
  'patient-workspace':        MENU_KEYS.PATIENT_WORKSPACE,
  'my-work':                  MENU_KEYS.MY_WORK,
  
  // Operational
  'financial-assistance':     MENU_KEYS.CREATE_ASSISTANCE_CASE,
  'assistance-cases':         MENU_KEYS.ASSISTANCE_CASES,
  'assistance-claims':        MENU_KEYS.ASSISTANCE_CLAIMS,
  'call-logs':                MENU_KEYS.CALL_LOGS,
  'adverse-events':           MENU_KEYS.ADVERSE_EVENTS,
  
  // Reports
  'reports':                  MENU_KEYS.REPORTS,
  'program-reports':          MENU_KEYS.REPORTS,
};

/**
 * Hook for program-level role-based menu access.
 *
 * This is the frontend counterpart to the backend's ProgramRoleMenuPermissionService.
 * It fetches the current user's accessible menu keys for a program and exposes
 * helper functions to check access before rendering any menu item or navigating to a route.
 *
 * Nothing is hardcoded; all access is driven by the database configuration.
 *
 * Usage:
 * ```tsx
 * const { canAccessMenu, programRoleName } = useProgramRoleMenuPermissions({ programId });
 * if (canAccessMenu('HOSPITALS')) { // show hospitals menu item }
 * ```
 */
export function useProgramRoleMenuPermissions({
  programId,
  enabled = true,
  ignoreSuperAdminFor,
}: UseProgramRoleMenuPermissionsOptions): ProgramRoleMenuPermissionsResult {
  const { user } = useAuth();

  // SuperAdmin bypasses all program-level menu checks, except for the menu
  // keys named in ignoreSuperAdminFor (e.g. opt-in workspace tabs), which
  // must follow the program's real configuration like any other role.
  const isSuperAdmin = user?.role === 'SuperAdmin' || user?.role === 'ProductAdmin';
  const restrictedKeys = useMemo(
    () => new Set<string>(ignoreSuperAdminFor ?? []),
    [ignoreSuperAdminFor],
  );
  const needsRealDataForSuperAdmin = isSuperAdmin && restrictedKeys.size > 0;

  const {
    data: response,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['programRoleMenuAccess', programId, user?.userId],
    queryFn: () => programRoleMenuPermissionService.getMyMenuAccess(programId!),
    enabled: enabled && !!programId && !!user && (!isSuperAdmin || needsRealDataForSuperAdmin),
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    retry: 1,
  });

  const menuAccess = response?.success ? response.data : null;
  const errorMessage = (isSuperAdmin && !needsRealDataForSuperAdmin)
    ? null
    : (response?.success === false
        ? response.message || 'Failed to load program access permissions.'
        : error instanceof Error
          ? error.message
          : error
            ? 'Failed to load program access permissions.'
            : null);

  const accessibleMenuKeys = useMemo<Set<string>>(() => {
    if (isSuperAdmin) {
      // SuperAdmin can access everything, except keys explicitly carved out
      // to follow the program's real configuration.
      const keys = new Set<string>(Object.values(MENU_KEYS));
      if (needsRealDataForSuperAdmin) {
        const realKeys = new Set(menuAccess?.accessibleMenuKeys ?? []);
        for (const key of restrictedKeys) {
          if (!realKeys.has(key)) keys.delete(key);
        }
      }
      return keys;
    }
    if (!menuAccess) return new Set<string>([MENU_KEYS.PROGRAM_DASHBOARD]);
    return new Set(menuAccess.accessibleMenuKeys ?? []);
  }, [menuAccess, isSuperAdmin, needsRealDataForSuperAdmin, restrictedKeys]);

  const canAccessMenu = (menuKey: MenuKey | string): boolean => {
    // Program dashboard is always accessible for any assigned user
    if (menuKey === MENU_KEYS.PROGRAM_DASHBOARD) return true;
    if (isSuperAdmin && !restrictedKeys.has(menuKey)) return true;
    return accessibleMenuKeys.has(menuKey);
  };

  const canAccessRoute = (routeSegment: string): boolean => {
    const menuKey = ROUTE_TO_MENU_KEY[routeSegment];
    if (!menuKey) return true; // Unknown routes default to accessible
    if (isSuperAdmin && !restrictedKeys.has(menuKey)) return true;
    return canAccessMenu(menuKey);
  };

  const permissionKeys = useMemo<Set<string>>(() => {
    if (isSuperAdmin) {
      return new Set<string>();
    }

    return new Set(menuAccess?.permissionKeys ?? []);
  }, [isSuperAdmin, menuAccess?.permissionKeys]);

  return {
    menuAccess,
    isLoading: (!isSuperAdmin || needsRealDataForSuperAdmin) && isLoading,
    error: errorMessage,
    hasRoleAssignment: !!menuAccess?.roleId,
    programRoleName: menuAccess?.roleName ?? user?.programRoleName ?? null,
    roleCategory: menuAccess?.roleCategory ?? user?.roleCategory ?? null,
    canAccessMenu,
    canAccessRoute,
    accessibleMenuKeys,
    refetch,
    // Governance properties
    governanceType: menuAccess?.governanceType ?? (isSuperAdmin ? 'ADMINISTRATIVE' : 'OPERATIONAL'),
    isConfigurationRole: menuAccess?.isConfigurationRole ?? false,
    isOperationalRole: menuAccess?.isOperationalRole ?? !isSuperAdmin,
    defaultLandingRoute: menuAccess?.defaultLandingRoute ?? (isSuperAdmin ? 'dashboard' : 'operational-dashboard'),
    dashboardKey: menuAccess?.dashboardKey ?? (isSuperAdmin ? 'dashboard' : 'operational-dashboard'),
    landingRoute: menuAccess?.landingRoute ?? '',
    permissionKeys,
  };
}
